import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import type { RootState } from '../../index';
import { ORGANIZATION_TYPE } from '../../main/organization/organizationType';
import type {
  Thread,
  GetThreadsParams,
  GetThreadsResponse,
  GetThreadByIdResponse,
  GetThreadMessagesParams,
  GetThreadMessagesResponse,
  CreateThreadParams,
  CreateThreadResponse,
  SendMessageParams,
  SendMessageResponse,
  UploadThreadFileParams,
  UploadThreadFileResponse,
} from './models';

const AI_AGENT_TAG = 'ai_agent';

/**
 * How many messages a paged surface opens a thread with, and takes per
 * scroll-up. Mirrors OrchestratorService::MESSAGES_PER_PAGE on the backend,
 * which caps a page at 100.
 */
export const THREAD_MESSAGES_PER_PAGE = 30;

/**
 * Custom baseQuery that dynamically resolves the role prefix for agent endpoints.
 * Reads `user.organization.type` from the Redux store and maps:
 *   - 'admin' → '/admin'
 *   - 'organizer' → '/organizer'
 *   - 'attendee' → '/attendee'
 *   - 'partner' (default) → '/partner'
 *
 * Endpoint URLs should start with `/agents/...` (no role prefix).
 */
const agentBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const state = api.getState() as RootState;
  const orgType = state?.userAuth?.user?.organization?.type;
  const rolePrefix =
    orgType === 'admin'
      ? '/admin'
      : orgType === ORGANIZATION_TYPE.organizer
        ? '/organizer'
        : orgType === ORGANIZATION_TYPE.attendee
          ? '/attendee'
          : '/partner';

  const adjustedArgs =
    typeof args === 'string'
      ? `${rolePrefix}${args}`
      : { ...args, url: `${rolePrefix}${args.url}` };

  return baseQueryWithReauth(adjustedArgs, api, extraOptions);
};

export const aiAgentApi = createApi({
  reducerPath: 'ai_agent',
  baseQuery: agentBaseQuery,
  tagTypes: [AI_AGENT_TAG],
  endpoints: (build) => ({
    getThreads: build.query<GetThreadsResponse, GetThreadsParams>({
      query: ({ page = 1, agent_type } = {}) => ({
        url: '/agents/threads',
        params: { page, ...(agent_type && { agent_type }) },
        headers: { Accept: 'application/json' },
      }),
      serializeQueryArgs: ({ queryArgs }) =>
        `getThreads-${queryArgs?.agent_type || 'default'}${queryArgs?.scope ? `-${queryArgs.scope}` : ''}`,
      merge: (currentCache, newResponse) => {
        if (newResponse.meta.current_page === 1) {
          return newResponse;
        }
        const existingIds = new Set(currentCache.data.map((t) => t.id));
        const uniqueNew = newResponse.data.filter((t) => !existingIds.has(t.id));
        return {
          ...newResponse,
          data: [...currentCache.data, ...uniqueNew],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
      providesTags: (_result, _error, arg) => [
        { type: AI_AGENT_TAG, id: 'LIST' },
        { type: AI_AGENT_TAG, id: `LIST-${arg?.agent_type || 'default'}` },
      ],
    }),

    // the thread itself — title, agent, status, context. Its messages have one
    // source, getThreadMessages below
    getThreadById: build.query<GetThreadByIdResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/agents/threads/${id}`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { id }) => [{ type: AI_AGENT_TAG, id }],
    }),

    // one page of a thread's messages: the newest page by default, `before_id`
    // walks up the history, `after_id` catches an open thread up with what was
    // said since. Never cached under the thread's tag — a page is a snapshot
    // the surface merges into its own list, not the thread's state
    getThreadMessages: build.query<GetThreadMessagesResponse, GetThreadMessagesParams>({
      query: ({ id, before_id, after_id, per_page }) => ({
        url: `/agents/threads/${id}/messages`,
        params: {
          ...(before_id ? { before_id } : {}),
          ...(after_id !== undefined ? { after_id } : {}),
          ...(per_page ? { per_page } : {}),
        },
        headers: { Accept: 'application/json' },
      }),
    }),

    createThread: build.mutation<CreateThreadResponse, CreateThreadParams>({
      query: (body) => ({
        url: '/agents/threads',
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: AI_AGENT_TAG, id: 'LIST' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          const cacheKey = { agent_type: arg.agent_type } as GetThreadsParams;
          dispatch(
            aiAgentApi.util.updateQueryData('getThreads', cacheKey, (draft) => {
              draft.data.unshift(response.data);
              draft.meta.total += 1;
            }),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),

    sendMessage: build.mutation<SendMessageResponse, SendMessageParams>({
      query: ({ threadId, message, context, file_ids }) => ({
        url: `/agents/threads/${threadId}/messages`,
        method: 'POST',
        body: { message, context, ...(file_ids?.length ? { file_ids } : {}) },
        headers: { Accept: 'application/json' },
      }),
    }),

    uploadThreadFile: build.mutation<UploadThreadFileResponse, UploadThreadFileParams>({
      query: ({ threadId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/agents/threads/${threadId}/files`,
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        };
      },
    }),

    deleteThread: build.mutation<{ data: Thread }, { id: number }>({
      query: ({ id }) => ({
        url: `/agents/threads/${id}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: AI_AGENT_TAG, id: 'LIST' }],
    }),

    stopThread: build.mutation<{ data: Thread }, { id: number }>({
      query: ({ id }) => ({
        url: `/agents/threads/${id}/stop`,
        method: 'POST',
        headers: { Accept: 'application/json' },
      }),
    }),
  }),
});

export const {
  useGetThreadsQuery,
  useGetThreadByIdQuery,
  useLazyGetThreadByIdQuery,
  useLazyGetThreadMessagesQuery,
  useCreateThreadMutation,
  useSendMessageMutation,
  useUploadThreadFileMutation,
  useDeleteThreadMutation,
  useStopThreadMutation,
} = aiAgentApi;
